import { GET_APP_NAME, BUNDLE_APP_NAME } from '../../app_constant';
import { USER_DEAFULT_KEY } from '../constant/event';

/**
 * Base PostHog super-properties for the web app. ONE implementation, shared by:
 *   - App.js `loaded` callback (runs right after posthog.init — this is the
 *     registration that actually sticks on a page load, see below),
 *   - AnalyticsProvider (re-registers on in-session exam switches / re-mounts),
 *   - the sign-out path in AuthProvider (posthog-js `reset()` clears persistence,
 *     super-properties included, so a real sign-out re-registers them for the
 *     new anonymous identity).
 *
 * `register()` is local-only (no event emitted) and a silent no-op before
 * `init()` — posthog-js/react's PostHogProvider initialises the client in its
 * OWN effect, which runs after every child effect, so a child effect's
 * register() on first mount is discarded. Keeping every caller on this helper
 * means it does not matter which one runs last.
 *
 * The saved exam lives under USER_DEAFULT_KEY.SAVED_BUNDLE_DETAILS (written by
 * BundleProvider.setSelectedBundle). Until 2026-09 the fallback here read a key
 * nothing ever wrote ('selectedCertificate'), so every page load registered the
 * brand's default exam and per-exam web analytics were wrong unless the user
 * switched exam mid-session.
 */

function readSavedExamKey(storage) {
  try {
    return storage?.getItem?.(USER_DEAFULT_KEY.SAVED_BUNDLE_DETAILS) || '';
  } catch (_) {
    // Storage can throw (Safari private mode, blocked site data). Fall through
    // to the brand default rather than break analytics boot.
    return '';
  }
}

function defaultStorage() {
  // `typeof localStorage` is NOT a safe probe: on `window` it is an accessor
  // that throws SecurityError when site data is blocked (Chrome "Block all
  // cookies", partitioned cross-site iframes). typeof only guards an undeclared
  // binding, not a throwing getter. This runs as a default-parameter
  // expression, before any function body, so it must catch for itself.
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch (_) {
    return undefined;
  }
}

/**
 * Resolve the exam key to report: explicit context value first, then the
 * persisted selection, else '' (GET_APP_NAME maps '' to the brand's default).
 */
export function resolveExamKey(selectedCertificate, storage = defaultStorage()) {
  return selectedCertificate || readSavedExamKey(storage);
}

export function registerBaseSuperProps(posthog, selectedCertificate, storage = defaultStorage()) {
  if (!posthog) return;
  const cert = resolveExamKey(selectedCertificate, storage);
  posthog.register({
    app_name: BUNDLE_APP_NAME,
    exam_name: GET_APP_NAME(cert),
    platform: 'web',
    source_environment: process.env.NODE_ENV || 'production',
    app_version: process.env.REACT_APP_APP_VERSION || 'dev',
    build_commit: process.env.REACT_APP_BUILD_COMMIT || 'unknown',
  });
}
